import { useEffect, useRef, type ReactNode } from "react";

/** Esc closes only the dialog on top (a picker opened from a dialog closes alone). */
export function isTopModal(el: HTMLElement | null): boolean {
  const all = document.querySelectorAll(".modal");
  return !!el && all[all.length - 1] === el;
}
import { Icon } from "./ui";

/** A dialog on the admin's modal styles: Esc and the scrim close it unless it is busy. */
export function Dialog({ title, children, onClose, wide, busy }: { title: string; children: ReactNode; onClose: () => void; wide?: boolean; busy?: boolean }) {
  const root = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy && isTopModal(root.current)) closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy]);
  return (
    <div ref={root} className="modal" role="dialog" aria-modal="true" aria-labelledby="dlg-title">
      <button type="button" className="modal__scrim" aria-label="Close" onClick={() => (busy ? undefined : onClose())} />
      <div className={`modal__card modal__card--form${wide ? " modal__card--wide" : ""}`}>
        <div className="modal__head">
          <h2 id="dlg-title">{title}</h2>
          <button type="button" className="btn btn--plain btn--icon" aria-label="Close" onClick={onClose} disabled={busy}>
            <Icon.close />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** A small confirm: says what happens, then Cancel or the action. */
export function ConfirmDialog({ open, title, text, confirmLabel, tone = "primary", busy, onConfirm, onCancel, children }: {
  open: boolean;
  title: string;
  text: ReactNode;
  confirmLabel: string;
  tone?: "primary" | "danger";
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}) {
  const first = useRef<HTMLButtonElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  useEffect(() => {
    if (open) first.current?.focus();
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy && isTopModal(root.current)) cancelRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy]);
  if (!open) return null;
  return (
    <div ref={root} className="modal" role="dialog" aria-modal="true" aria-labelledby="confirm-title">
      <button type="button" className="modal__scrim" aria-label="Close" onClick={() => (busy ? undefined : onCancel())} />
      <div className="modal__card">
        <h2 id="confirm-title">{title}</h2>
        <p>{text}</p>
        {children}
        <div className="modal__actions">
          <button type="button" className="btn" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="button" ref={first} className={`btn ${tone === "danger" ? "btn--danger" : "btn--primary"}`} onClick={onConfirm} disabled={busy}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
}
